<template>
  <q-dialog
    v-model="showLogoutDialog"
    backdrop-filter="blur(4px) saturate(150%)"
  >
    <div style="position: relative; max-width: 500px; width: 90vw;">
      <base-card>
        <q-card-section>
          <div class="text-h6">{{ t(localizationKeys.inactivity) }}</div>
        </q-card-section>

        <q-card-section>
          <div class="flex justify-center">
            <q-btn
              :label="t(localizationKeys.logIn)"
              color="primary"
              data-cy="LoginButton"
              style="width: 130px"
              @click="async () => login(keycloakPromise)"
            />
          </div>
        </q-card-section>
      </base-card>
    </div>
  </q-dialog>
  <router-view/>
</template>

<script setup lang="ts">
import { provide, readonly, ref, watch } from "vue";
import Keycloak from "keycloak-js";
import {ApiProvider} from "src/utils/ApiProvider";
import {useQuasar} from "quasar";
import {useSettingsStore} from "stores/settings";
import {settingKeys} from "src/settings/SettingsSpec";
import {useI18n} from "vue-i18n";
import {assertDefined} from "src/utils/Validation";
import {useAuthStatusStore} from "stores/useAuthentificationStatusStore";
import localizationKeys from "src/i18n/localizationKeys";
import BaseCard from "components/ui/BaseCard.vue";
import { login } from "src/utils/Authentication";

const {t} = useI18n();
const $q = useQuasar();
const settingsStore = useSettingsStore();
const {locale} = useI18n();
const authStatusStore = useAuthStatusStore();

watch(settingsStore.settings, () => {
  $q.dark.set(settingsStore.settings.get(settingKeys.useDarkMode) as boolean);
}, {
  immediate: true,
});
watch(settingsStore.settings, () => {
  locale.value = settingsStore.settings.get(settingKeys.language) as string;
}, {
  immediate: true,
});

const keycloakPromise = initKeycloak();
const apiProviderPromise = keycloakPromise.then(
    (keycloak: Keycloak) => new ApiProvider(keycloak),
);
provide("keycloakPromise", readonly(keycloakPromise));
provide("apiProviderPromise", readonly(apiProviderPromise));

const showLogoutDialog = ref(false);

async function initKeycloak(): Promise<Keycloak> {
  const keycloak = new Keycloak({
    realm: "club",
    url: import.meta.env.VITE_KEYCLOAK_BASE_URL ?? "/auth",
    clientId: "public",
  });
  keycloak.onAuthRefreshError = () => {
    authStatusStore.activeAuthStatus = false;
    showLogoutDialog.value = true;
  }
  return keycloak
      .init({
        onLoad: "check-sso",
      })
      .then(() => {
        authStatusStore.activeAuthStatus = keycloak.authenticated ?? false;
        sessionStorage.setItem("keycloakLoginUrl", keycloak.createLoginUrl())
        sessionStorage.setItem("userId", keycloak.subject ?? "");
        if (keycloak.authenticated) {
          keycloak.loadUserProfile().then(async profile => {
            const organizerApi = await apiProviderPromise.then(apiProvider => apiProvider.organizerApi);
            await organizerApi.addOrganizer({
              organizerId: assertDefined(profile.id),
              firstName: assertDefined(profile.firstName),
              lastName: assertDefined(profile.lastName),
              club: assertDefined(profile.attributes?.clubName[0] ?? "")
            });
          })
        }
        return keycloak;
      });
}
</script>
